import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import POS from './pages/POS';
import Menu from './pages/Menu';
import Orders from './pages/Orders';
import Tables from './pages/Tables';
import Reservations from './pages/Reservations';
import KDS from './pages/KDS';
import Inventory from './pages/Inventory';
import Customers from './pages/Customers';
import Staff from './pages/Staff';
import Payroll from './pages/Payroll';
import Riders from './pages/Riders';
import Analytics from './pages/Analytics';
import AIInsights from './pages/AIInsights';
import Warehouse from './pages/Warehouse';
import Expenses from './pages/Expenses';
import UserManagement from './pages/UserManagement';
import OutletManagement from './pages/OutletManagement';
import QRCode from './pages/QRCode';
import Settings from './pages/Settings';
import Billing from './pages/Billing';
import PublicOrder from './pages/PublicOrder';
import Closing from './pages/Closing';

// Role lists here mirror Layout.js's SECTIONS exactly — the sidebar hides a
// link, but only this guard actually stops someone from landing on the page
// by typing the URL directly (the backend API is the real security boundary;
// this just keeps the UI from looking broken/empty for a role that can't
// use the page). Dashboard is the only route open to every role.
const ADMIN_MANAGER = ['super_admin', 'admin', 'manager'];
const ADMIN_MANAGER_FLOOR = ['super_admin', 'admin', 'manager', 'cashier', 'waiter'];

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/order/:code" element={<PublicOrder />} />
        <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index element={<Dashboard />} />
          {/* POS is cashier-station only — waiters use the Orders page to punch orders */}
          <Route path="pos" element={
            <PrivateRoute roles={['super_admin', 'admin', 'manager', 'cashier']}>
              <POS />
            </PrivateRoute>
          } />
          <Route path="menu" element={<PrivateRoute roles={ADMIN_MANAGER}><Menu /></PrivateRoute>} />
          <Route path="orders" element={<PrivateRoute roles={ADMIN_MANAGER_FLOOR}><Orders /></PrivateRoute>} />
          <Route path="tables" element={<PrivateRoute roles={ADMIN_MANAGER_FLOOR}><Tables /></PrivateRoute>} />
          <Route path="reservations" element={<PrivateRoute roles={ADMIN_MANAGER_FLOOR}><Reservations /></PrivateRoute>} />
          <Route path="kds" element={
            <PrivateRoute roles={['super_admin', 'admin', 'manager', 'chef']}>
              <KDS />
            </PrivateRoute>
          } />
          <Route path="inventory" element={<PrivateRoute roles={ADMIN_MANAGER}><Inventory /></PrivateRoute>} />
          <Route path="customers" element={<PrivateRoute roles={ADMIN_MANAGER_FLOOR}><Customers /></PrivateRoute>} />
          <Route path="staff" element={
            <PrivateRoute roles={['super_admin', 'admin', 'manager', 'staff', 'cashier', 'waiter']}>
              <Staff />
            </PrivateRoute>
          } />
          <Route path="payroll" element={<PrivateRoute roles={ADMIN_MANAGER}><Payroll /></PrivateRoute>} />
          <Route path="riders" element={
            <PrivateRoute roles={['super_admin', 'admin', 'manager', 'rider']}>
              <Riders />
            </PrivateRoute>
          } />
          <Route path="analytics" element={<PrivateRoute roles={ADMIN_MANAGER}><Analytics /></PrivateRoute>} />
          <Route path="ai" element={<PrivateRoute roles={ADMIN_MANAGER}><AIInsights /></PrivateRoute>} />
          <Route path="warehouse" element={<PrivateRoute roles={ADMIN_MANAGER}><Warehouse /></PrivateRoute>} />
          <Route path="expenses" element={<PrivateRoute roles={ADMIN_MANAGER}><Expenses /></PrivateRoute>} />
          <Route path="closing" element={<PrivateRoute roles={ADMIN_MANAGER}><Closing /></PrivateRoute>} />
          <Route path="users" element={
            <PrivateRoute roles={['super_admin', 'admin']}>
              <UserManagement />
            </PrivateRoute>
          } />
          <Route path="outlets" element={
            <PrivateRoute roles={['super_admin']}>
              <OutletManagement />
            </PrivateRoute>
          } />
          <Route path="qrcode" element={<PrivateRoute roles={ADMIN_MANAGER}><QRCode /></PrivateRoute>} />
          <Route path="settings" element={
            <PrivateRoute roles={['super_admin', 'admin']}>
              <Settings />
            </PrivateRoute>
          } />
          {/* Open to every role: this is also where an expired/past-due
              tenant gets redirected to from anywhere in the app, and staff
              without billing permissions still need to see why they're
              locked out even if only an admin can act on it. */}
          <Route path="billing" element={<Billing />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
